// tutti-server is published on the tutti.sh vhosts only, so the Tutti Computer
// admin surface is reachable only from a console whose API origin lands there.
// admin.tutti.sh and admin-staging.tutti.sh qualify because the Worker proxies
// their /api/* to https://api.tutti.sh (see worker/edge-context.ts). Legacy
// consoles call their own brand host cross-origin, where no tutti-server route
// matches.
declare const __PPE_DEV_LANE__: string;

const LEGACY_DOMAIN_CONFIG = {
  publicAppOrigin: 'https://admin.svenzeng.com',
  adminApiOrigin: 'https://svenzeng.com',
  authDomain: 'auth.svenzeng.com',
  accountApiOrigin: 'https://svenzeng.com/api/account',
  accountAppId: 'admin',
  accountCallbackOrigin: 'https://admin.svenzeng.com',
  projectShareBaseUrl: 'https://svenzeng.com/p/',
  tuttiServerApiReachable: false,
} as const;

const NEXTOP_DOMAIN_CONFIG = {
  publicAppOrigin: 'https://admin.nextop.sh',
  adminApiOrigin: 'https://nextop.sh',
  authDomain: 'auth.nextop.sh',
  accountApiOrigin: 'https://nextop.sh/api/account',
  accountAppId: 'admin',
  accountCallbackOrigin: 'https://admin.nextop.sh',
  projectShareBaseUrl: 'https://nextop.sh/p/',
  tuttiServerApiReachable: false,
} as const;

const TUTTI_DOMAIN_CONFIG = {
  publicAppOrigin: 'https://admin.tutti.sh',
  adminApiOrigin: 'https://admin.tutti.sh',
  authDomain: 'auth.tutti.sh',
  accountApiOrigin: 'https://admin.tutti.sh/api/account',
  accountAppId: 'admin',
  accountCallbackOrigin: 'https://admin.tutti.sh',
  projectShareBaseUrl: 'https://tutti.sh/p/',
  tuttiServerApiReachable: true,
} as const;

const TUTTI_STAGING_DOMAIN_CONFIG = {
  publicAppOrigin: 'https://admin-staging.tutti.sh',
  adminApiOrigin: 'https://admin-staging.tutti.sh',
  authDomain: 'auth.tutti.sh',
  accountApiOrigin: 'https://admin-staging.tutti.sh/api/account',
  accountAppId: 'admin',
  accountCallbackOrigin: 'https://admin-staging.tutti.sh',
  projectShareBaseUrl: 'https://staging.tutti.sh/p/',
  tuttiServerApiReachable: true,
} as const;

function normalizeHostname(hostname?: string): string {
  return (hostname ?? '').trim().toLowerCase();
}

function hostInDomainFamily(hostname: string, domainRoot: string): boolean {
  return hostname === domainRoot || hostname.endsWith(`.${domainRoot}`);
}

function currentHostname(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return normalizeHostname(window.location.hostname);
}

export function resolveDomainConfig(hostname = currentHostname()) {
  const normalized = normalizeHostname(hostname);
  if (
    typeof __PPE_DEV_LANE__ !== 'undefined' &&
    __PPE_DEV_LANE__ &&
    (normalized === 'localhost' || normalized === '127.0.0.1')
  ) {
    const localOrigin = window.location.origin as
      | 'http://localhost:4000'
      | 'http://127.0.0.1:4000';
    return {
      ...TUTTI_DOMAIN_CONFIG,
      publicAppOrigin: localOrigin,
      adminApiOrigin: localOrigin,
      accountApiOrigin: `${localOrigin}/api/account`,
      accountCallbackOrigin: localOrigin,
    } as const;
  }
  if (hostInDomainFamily(normalized, 'nextop.sh')) {
    return NEXTOP_DOMAIN_CONFIG;
  }
  if (normalized === 'admin-staging.tutti.sh') {
    return TUTTI_STAGING_DOMAIN_CONFIG;
  }
  if (hostInDomainFamily(normalized, 'tutti.sh')) {
    return TUTTI_DOMAIN_CONFIG;
  }
  return LEGACY_DOMAIN_CONFIG;
}

export type DomainConfig = ReturnType<typeof resolveDomainConfig>;

const domainConfig = resolveDomainConfig();

export const PUBLIC_APP_ORIGIN = domainConfig.publicAppOrigin;
export const ADMIN_API_ORIGIN = domainConfig.adminApiOrigin;
export const AUTH_DOMAIN = domainConfig.authDomain;
export const ACCOUNT_API_ORIGIN = domainConfig.accountApiOrigin;
export const ACCOUNT_APP_ID = domainConfig.accountAppId;
export const ACCOUNT_CALLBACK_ORIGIN = domainConfig.accountCallbackOrigin;
export const PROJECT_SHARE_BASE_URL = domainConfig.projectShareBaseUrl;
export const TUTTI_SERVER_API_REACHABLE = domainConfig.tuttiServerApiReachable;

export function buildProjectUrl(projectId: string) {
  return `${PROJECT_SHARE_BASE_URL}${encodeURIComponent(projectId)}`;
}
